import { Link, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Play, UserMinus, RefreshCw, Eye, UserPlus, UserX, CheckCircle2 } from "lucide-react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { appointment_status } from "@/models/appointment.model";
import { toast } from "sonner";
import { AppointmentApprovalService } from "@/services/appointment-approval.service";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useEffect } from "react";
import { AcceptJobDialog } from "@/components/shared/accept-job-dialog";
import { AppointmentsRepository } from "@/repositories/appointments.repository";
import { HAND_BACK_REASON_MAX_LENGTH } from "@shyne/shared/api";
import { useFieldStaffRowActions } from "@/hooks/useFieldStaffRowActions";
import { useSubsAppointmentActions } from "../hooks/useSubsAppointmentActions";
import SimpleAssignmentModal from "./SimpleAssignmentModal";

interface IndividualAppointmentActionsDropdownProps {
  appointment: AppointmentJoin;
  onAppointmentUpdate?: (updatedAppointment: AppointmentJoin) => void;
  onRefetch?: () => void | Promise<void>;
}

export default function IndividualAppointmentActionsDropdown({
  appointment,
  onAppointmentUpdate,
  onRefetch,
}: IndividualAppointmentActionsDropdownProps) {
  const navigate = useNavigate();
  const { accept, handBack, handingBack } = useFieldStaffRowActions({ appointment, onAppointmentUpdate, onRefetch });

  // Dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Use the assignment actions hook
  const {
    assigningIndividual,
    handleAssignIndividual,
    handleUnassignIndividual,
    mayAssignWorker,
    mayUnassignWorker,
  } = useSubsAppointmentActions({
    appointment,
    onAppointmentUpdate,
    onModalClose: () => setAssignmentModalOpen(false),
  });

  // Dialog states
  const [showReassignmentDialog, setShowReassignmentDialog] = useState(false);
  const [showServiceChangeDialog, setShowServiceChangeDialog] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);

  // Cleanup effect to ensure modal state is reset
  useEffect(() => {
    return () => {
      setAssignmentModalOpen(false);
    };
  }, []);

  // Form states
  const [reassignmentReason, setReassignmentReason] = useState("");
  const [selectedParent, setSelectedParent] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [selectedServiceName, setSelectedServiceName] = useState("");

  // Loading states
  const [parentCategories, setParentCategories] = useState<string[]>([]);
  const [availableServices, setAvailableServices] = useState<{ id: string; name: string }[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Load parent categories for service change
  const loadParentCategories = async () => {
    if (!appointment.vendor.id) {
      console.error("No vendor ID available for loading parent categories");
      return;
    }

    setLoadingServices(true);
    try {
      const categories = await AppointmentsRepository.listParentCategoriesForVendor(appointment.vendor.id);
      setParentCategories(categories);
    } catch (error) {
      console.error("Failed to load parent categories:", error);
      setParentCategories([]);
    } finally {
      setLoadingServices(false);
    }
  };

  // Load services when parent is selected
  const loadChildServices = async () => {
    setSelectedService("");
    setSelectedServiceName("");
    setAvailableServices([]);

    if (!selectedParent || !appointment.vendor.id) return;

    setLoadingServices(true);
    try {
      const services = await AppointmentsRepository.listChildServices(appointment.vendor.id, selectedParent);
      setAvailableServices(services);
    } catch (error) {
      console.error("Failed to load services:", error);
      setAvailableServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  // Load child services when parent category changes
  useEffect(() => {
    if (selectedParent) {
      loadChildServices();
    }
  }, [selectedParent]);

  const handleNavigate = (path: string) => {
    setDropdownOpen(false); // Close dropdown first
    navigate(path);
  };

  const handleUnassignIndividualClick = async () => {
    setDropdownOpen(false); // Close dropdown first
    setTimeout(() => {
      handleUnassignIndividual();
    }, 0);
  };

  const handleRequestReassignment = () => {
    setDropdownOpen(false); // Close dropdown first
    setTimeout(() => {
      setReassignmentReason("");
      setShowReassignmentDialog(true);
    }, 0);
  };

  const handleRequestServiceChange = () => {
    setDropdownOpen(false); // Close dropdown first
    setTimeout(() => {
      setSelectedParent("");
      setSelectedService("");
      setSelectedServiceName("");
      setParentCategories([]);
      setAvailableServices([]);
      setShowServiceChangeDialog(true);
      loadParentCategories();
    }, 0);
  };

  const handleAssignIndividualClick = () => {
    setDropdownOpen(false); // Close dropdown first
    setTimeout(() => {
      setAssignmentModalOpen(true);
    }, 0);
  };

  const submitReassignmentRequest = () => handBack(reassignmentReason, () => setShowReassignmentDialog(false));

  const submitServiceChangeRequest = async () => {
    if (!selectedService || !selectedServiceName || !selectedParent) {
      toast.error("Please select a service");
      return;
    }

    try {
      await AppointmentApprovalService.requestServiceChangeAsAssignee(appointment.id, selectedService);

      toast.success("Service change request sent successfully");
      setShowServiceChangeDialog(false);
    } catch (error) {
      console.error("Failed to send service change request:", error);
      toast.error(fieldActionMessage(error, "Failed to send service change request"));
    }
  };

  // Determine available actions based on current status
  const availableActions = [];

  // For ASSIGNED status: only show Accept Appointment and Request Reassignment
  if (appointment.status === appointment_status.assigned) {
    // Accept Appointment - changes status to accepted
    availableActions.push({
      key: "accept",
      label: "Accept Appointment",
      icon: <CheckCircle2 className="w-4 h-4" />,
      action: () => {
        setDropdownOpen(false);
        setShowAcceptDialog(true);
      },
      className: "text-green-600",
    });

    // Request Reassignment
    availableActions.push({
      key: "request-reassignment",
      label: "Request Reassignment",
      icon: <UserMinus className="w-4 h-4" />,
      action: () => handleRequestReassignment(),
      className: "text-amber-600",
    });
  }

  if (appointment.status === appointment_status.accepted) {
    if (mayUnassignWorker) {
      availableActions.push({
        key: "unassign-individual",
        label: "Unassign Individual",
        icon: <UserX className="w-4 h-4" />,
        action: handleUnassignIndividualClick,
        className: "text-red-600",
      });
    }
    if (mayAssignWorker) {
      availableActions.push({
        key: "assign-individual",
        label: "Assign Individual",
        icon: <UserPlus className="w-4 h-4" />,
        action: handleAssignIndividualClick,
        className: "text-green-600",
      });
    }

    availableActions.push({
      key: "request-service-change",
      label: "Request Service Change",
      icon: <RefreshCw className="w-4 h-4" />,
      action: () => handleRequestServiceChange(),
      className: "text-blue-600",
    });
  }

  // Continue Work - for appointments already in progress
  if (appointment.status === appointment_status.inprogress) {
    availableActions.push({
      key: "continue",
      label: "Continue Work",
      icon: <Play className="w-4 h-4" />,
      action: () => handleNavigate(`/appointments/${appointment.id}`),
      className: "text-blue-600",
    });
  }

  // Start Work - available for goback status (individual/sub starts fresh work)
  if (appointment.status === appointment_status.goback) {
    availableActions.push({
      key: "start-work",
      label: "Start Work",
      icon: <Play className="w-4 h-4" />,
      action: () => handleNavigate(`/appointments/${appointment.id}`),
      className: "text-green-600",
    });
  }

  // Request Reassignment - available for pending and rescheduled statuses (not goback)
  if (
    [
      appointment_status.pending,
      appointment_status.rescheduled,
    ].includes(appointment.status)
  ) {
    availableActions.push({
      key: "request-reassignment",
      label: "Request Reassignment",
      icon: <UserMinus className="w-4 h-4" />,
      action: () => handleRequestReassignment(),
      className: "text-amber-600",
    });
  }

  if ([appointment_status.pending, appointment_status.rescheduled].includes(appointment.status)) {
    availableActions.push({
      key: "request-service-change",
      label: "Request Service Change",
      icon: <RefreshCw className="w-4 h-4" />,
      action: () => handleRequestServiceChange(),
      className: "text-blue-600",
    });
  }

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/appointments/${appointment.id}`}>
              <Eye className="w-4 h-4" />
              <span className="ml-2">View Details</span>
            </Link>
          </DropdownMenuItem>

          {availableActions.length > 0 && <DropdownMenuSeparator />}

          {availableActions.map((action) => (
            <DropdownMenuItem key={action.key} onClick={action.action} className={action.className}>
              {action.icon}
              <span className="ml-2">{action.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Reassignment Dialog */}
      <Dialog open={showReassignmentDialog} onOpenChange={setShowReassignmentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus className="w-5 h-5" />
              Request Reassignment
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                placeholder="Please explain why you need to be reassigned..."
                value={reassignmentReason}
                onChange={(e) => setReassignmentReason(e.target.value)}
                maxLength={HAND_BACK_REASON_MAX_LENGTH}
                rows={3}
              />
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
              <p className="text-sm text-orange-800">
                This will send a reassignment request to the vendor for approval. They can approve and assign a new
                individual, or reject your request.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setShowReassignmentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={submitReassignmentRequest} disabled={handingBack}>
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Change Dialog */}
      <Dialog open={showServiceChangeDialog} onOpenChange={setShowServiceChangeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Request Service Change
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Request a service change from the PMC. This requires approval from the property management company.
            </p>

            {loadingServices && parentCategories.length === 0 ? (
              <div className="text-sm text-muted-foreground">Loading service categories...</div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Service Category</Label>
                  <Select value={selectedParent} onValueChange={setSelectedParent}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select service category" />
                    </SelectTrigger>
                    <SelectContent>
                      {parentCategories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Specific Service</Label>
                  <Select
                    value={selectedService}
                    onValueChange={(value) => {
                      setSelectedService(value);
                      const service = availableServices.find((s) => s.id === value);
                      setSelectedServiceName(service?.name || "");
                    }}
                    disabled={!selectedParent}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select specific service" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingServices && selectedParent ? (
                        <SelectItem value="loading" disabled>
                          Loading services...
                        </SelectItem>
                      ) : (
                        availableServices.map((service) => (
                          <SelectItem key={service.id} value={service.id}>
                            {service.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                Service change requests require approval from the PMC. They will be notified of your request.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setShowServiceChangeDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitServiceChangeRequest}
              disabled={!selectedService || !selectedServiceName || !selectedParent}
            >
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Modal - Simple implementation */}
      <SimpleAssignmentModal
        isOpen={assignmentModalOpen}
        onClose={() => {
          console.log("IndividualAppointmentActionsDropdown: Modal close requested");
          setAssignmentModalOpen(false);
        }}
        appointment={appointment}
        onAssign={handleAssignIndividual}
        isLoading={assigningIndividual}
      />

      <AcceptJobDialog
        open={showAcceptDialog}
        onOpenChange={setShowAcceptDialog}
        onConfirm={() => {
          setShowAcceptDialog(false);
          void accept();
        }}
      />
    </>
  );
}
